import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Analytical hierarchy: labour (carbon) > certification (blue) > training/tooling (greys)
const CHART_COLORS = ['var(--carbon-800)', 'var(--status-info)', 'var(--carbon-500)', 'var(--border-default)'];

/** Donut chart + legend for the four cost categories. */
export function CostBreakdownChart({ costBreakdown, formatCost }) {
  const { t } = useTranslation();

  const pieData = [
    { name: t('advanced.labor'), value: costBreakdown.labor },
    { name: t('advanced.audit'), value: costBreakdown.audit },
    { name: t('advanced.training'), value: costBreakdown.training },
    { name: t('advanced.tools'), value: costBreakdown.tools },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#DDE1E5] rounded-lg px-3 py-2 shadow-card">
        <div className="text-[#171A1D] text-sm font-medium">{payload[0].name}</div>
        <div className="text-[#527F19] text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {formatCost(payload[0].value)}
        </div>
      </div>
    );
  };

  return (
    <Card className="bg-white border-[#DDE1E5] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider">
          {t('results.costBreakdown')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48" data-testid="cost-breakdown-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart title={t('results.costBreakdown')} desc={pieData.map(d => `${d.name}: ${formatCost(d.value)}`).join(', ')}>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" stroke="none" isAnimationActive={false}>
                {pieData.map((slice, i) => (
                  <Cell key={slice.name} fill={CHART_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 mt-2">
          {pieData.map((d, i) => (
            <div key={d.name} className="flex items-center gap-2 text-xs min-w-0">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i] }} />
              <span className="text-[#66707A] min-w-0">{d.name}</span>
              <span className="ml-auto shrink-0 text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {formatCost(d.value)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
